import { useEffect, useState } from 'react';
import { Card, CheckBox, ContentSwitcher } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { useAmplitude } from 'hooks/useAmplitude';
import { TDurationDetails } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DurationFilter } from '../DurationFilter/DurationFilter';
import { MIN_REPAYMENT_AMOUNT } from '../filterConfigs.helper';
import { OverpaymentAmountFilter } from '../OverpaymentAmountFilter/OverpaymentAmountFilter';
import styles from './OverpaymentOptions.module.css';

type TProps = {
  showOverpayment: boolean;
  maxTermLength: number;
  overpaymentAmount?: string;
  onOverpaymentDurationChange: (duration: TDurationDetails) => void;
  onOverpaymentChange: (amount?: string) => void;
  setShowOverpayment: (shown: boolean) => void;
};

type TOverpaymentOption = 'timeframe' | 'repaymentAmount';

export const OverpaymentOptions = ({
  showOverpayment,
  maxTermLength,
  overpaymentAmount,
  onOverpaymentDurationChange,
  onOverpaymentChange,
  setShowOverpayment,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const [selectedOverpaymentOption, setSelectedOverpaymentOption] =
    useState<TOverpaymentOption>('timeframe');

  useEffect(() => {
    if (showOverpayment) {
      // If timeframe is selected, set overpayment amount to undefined (as timeframe and repaymentAmount can not be used together)
      if (selectedOverpaymentOption === 'timeframe') {
        onOverpaymentChange(undefined);
        return;
      }

      // If repaymentAmount is selected, set endDate to null and overpayment amount to default min value
      const data = {
        startDate: null,
        endDate: null,
        duration: maxTermLength.toString(),
      };

      onOverpaymentDurationChange(data);
      onOverpaymentChange(MIN_REPAYMENT_AMOUNT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOverpayment, selectedOverpaymentOption]);

  const onCalculateOverpaymentClick = () => {
    // Reset custom values back to default if user unselects the calculate overpayment option
    if (showOverpayment) {
      const data = {
        startDate: null,
        endDate: null,
        duration: maxTermLength.toString(),
      };

      onOverpaymentChange(undefined);
      onOverpaymentDurationChange(data);
    }

    trackEvent(AMPLITUDE_EVENTS.calculateOverpaymentClicked);
    setShowOverpayment(!showOverpayment);
  };

  const OVERPAYMENT_OPTIONS: TOverpaymentOption[] = [
    'timeframe',
    'repaymentAmount',
  ];

  return (
    <Card className={styles.card} id="overpayment-options">
      <CheckBox
        label={t`Calculate early repayments`}
        onChange={onCalculateOverpaymentClick}
        data-testid="overpayment-checkbox"
        checked={showOverpayment}
      />
      <div
        className={clsx(styles.earlyRepaymentOptions, {
          [styles.disabled]: !showOverpayment,
        })}
      >
        <ContentSwitcher
          data-testid="overpayment-option"
          value={selectedOverpaymentOption}
          className={styles.contentSwitcher}
        >
          {OVERPAYMENT_OPTIONS.map((option) => (
            <ContentSwitcher.Button
              key={option}
              id={option}
              className={styles.contentSwitcherButton}
              setSelected={() => setSelectedOverpaymentOption(option)}
            >
              {option === 'timeframe' ? (
                <Trans>Repayment period</Trans>
              ) : (
                <Trans>Repayment amount</Trans>
              )}
            </ContentSwitcher.Button>
          ))}
        </ContentSwitcher>

        {selectedOverpaymentOption === 'timeframe' && (
          <DurationFilter
            maxTermLength={maxTermLength}
            isDisabled={!showOverpayment}
            onChange={onOverpaymentDurationChange}
          />
        )}
        {selectedOverpaymentOption === 'repaymentAmount' && (
          <OverpaymentAmountFilter
            value={overpaymentAmount}
            onChange={onOverpaymentChange}
          />
        )}
      </div>
    </Card>
  );
};
